import { Suspense, useState } from 'react'

import {
  AppId,
  InnosErrorBoundary,
  getIsWindowMode
} from '@inno-tools/container-bridge'
import { Spinner } from '@inno-ui/spinner'
import { useAvailableApps } from 'modules/app-registry/hooks/useAvailableApps'
import { AppSessionRouter } from 'modules/desktop/components/AppSessionRouter'
import { DevDrawer } from 'modules/desktop/components/DevDrawer'
import { getFilteredAppConfigs } from 'modules/desktop/utils/appConfigUtils'
import { Route, Routes, useLocation } from 'react-router-dom'
import { appConfigs } from 'shared/configs/appConfigs'
import { BridgedLiveChatSDKProvider } from 'shared/contexts'
import { useUserContext } from 'shared/hooks'
import { isProd } from 'shared/utils/isProd'
import { ProtectedApp } from 'src/shared/components'

import { LiveChatWidgets } from './modules/widgets/components'
import { MainPage } from './pages'
import { DetachedAppPage } from './pages/DetachedAppPage'
import { MyInvoicesPage } from './pages/MyInvoicesPage'

const isAppSessionPath = (pathname: string) =>
  pathname.startsWith(`${appConfigs[AppId.AFTER_SALES].path}/app-session`)

export const DesktopRouter = () => {
  const { user } = useUserContext()
  const { apps } = useAvailableApps()
  const { pathname } = useLocation()
  // Light wrapper only if opened on a session; one-way, set during render.
  const [isAppSession, setIsAppSession] = useState(() =>
    isAppSessionPath(pathname)
  )
  if (isAppSession && !isAppSessionPath(pathname)) {
    setIsAppSession(false)
  }

  if (isAppSession) {
    return (
      <BridgedLiveChatSDKProvider user={user}>
        <InnosErrorBoundary debugName='AppSession'>
          <AppSessionRouter />
        </InnosErrorBoundary>
      </BridgedLiveChatSDKProvider>
    )
  }

  const filteredAppConfigs = getFilteredAppConfigs(apps)
  const mainContent = (
    <Suspense fallback={<Spinner />}>
      <Routes>
        {filteredAppConfigs.map(([key, appConfig]) => (
          <Route
            key={key}
            path={`${appConfig.path}/*`}
            element={
              <ProtectedApp>
                <DetachedAppPage appConfig={appConfig} />
              </ProtectedApp>
            }
          />
        ))}

        <Route
          path='/system-actions/accounting/invoices'
          element={<MyInvoicesPage />}
        />
        <Route path='*' element={<MainPage />} />
      </Routes>
    </Suspense>
  )

  return (
    <BridgedLiveChatSDKProvider user={user}>
      {mainContent}
      {!getIsWindowMode() && !isProd() && <DevDrawer />}
      <InnosErrorBoundary debugName='LiveChatWidgets'>
        <LiveChatWidgets />
      </InnosErrorBoundary>
    </BridgedLiveChatSDKProvider>
  )
}
